import { ArrowRight, Binoculars, ExternalLink, Eye, Lightbulb, MapPin, Sparkles } from 'lucide-react';
import { resourceLinks } from '../data';
import type { ReadingSection, ResourceLink, TravelChapter } from '../types';

function resourcesFor(section: ReadingSection): ResourceLink[] {
  return resourceLinks.filter((link) => section.resourceIds?.includes(link.id));
}

function uniqueChapterResources(chapter: TravelChapter): ResourceLink[] {
  const ids = new Set(chapter.sections.flatMap((section) => section.resourceIds ?? []));
  return resourceLinks.filter((link) => ids.has(link.id));
}

function SpreadResources({ section }: { section: ReadingSection }) {
  const links = resourcesFor(section);
  if (links.length === 0) return null;

  return (
    <div className="spread-resources" aria-label={`${section.title} related resources`}>
      <b>继续了解</b>
      {links.map((link) => (
        <a href={link.url} key={link.id} rel="noreferrer" target="_blank">
          <span>{link.source}</span>
          {link.title}
          <ExternalLink aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}

function BookSpread({ index, section }: { index: number; section: ReadingSection }) {
  return (
    <article className={`book-spread spread-${section.id}`}>
      <figure className="spread-media">
        {section.image && <img key={section.image} src={section.image} alt={section.imageAlt} />}
        {section.imageCredit && <figcaption>{section.imageCredit}</figcaption>}
      </figure>

      <div className="spread-copy">
        <span className="spread-number">
          {String(index + 1).padStart(2, '0')} · {section.eyebrow}
        </span>
        <h2>{section.title}</h2>
        <p className="spread-deck">{section.deck}</p>
        {section.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.facts && (
          <div className="fact-ribbon" aria-label={`${section.title} key facts`}>
            {section.facts.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </div>
        )}

        {(section.lookFor || section.whyItMatters) && (
          <div className="spread-learning-grid">
            {section.lookFor && (
              <section className="learning-card">
                <h3>
                  <Eye aria-hidden="true" />
                  到现场怎么看
                </h3>
                <ul>
                  {section.lookFor.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            {section.whyItMatters && (
              <section className="learning-card">
                <h3>
                  <Lightbulb aria-hidden="true" />
                  为什么重要
                </h3>
                <p>{section.whyItMatters}</p>
                {section.fieldQuestion && <b>{section.fieldQuestion}</b>}
              </section>
            )}
          </div>
        )}

        {section.callout && (
          <aside className="parent-note">
            <Sparkles aria-hidden="true" />
            <div>
              <b>{section.callout.title}</b>
              <p>{section.callout.text}</p>
            </div>
          </aside>
        )}

        <SpreadResources section={section} />
      </div>
    </article>
  );
}

function ChapterLibrary({ chapter }: { chapter: TravelChapter }) {
  const links = uniqueChapterResources(chapter);
  if (links.length === 0) return null;

  return (
    <section className="resource-shelf chapter-library" aria-label={`${chapter.title} resource library`}>
      <div className="panel-heading">
        <Binoculars aria-hidden="true" />
        <div>
          <h2>本章延伸资料</h2>
          <p>孩子可以先看图和标题；家长可点开资料，继续补充地理、动植物和人文背景。</p>
        </div>
      </div>
      <div className="resource-grid">
        {links.map((link) => (
          <a className="resource-card" href={link.url} key={link.id} rel="noreferrer" target="_blank">
            <span className="resource-meta">
              <b>{link.audience}</b>
              <small>{link.source}</small>
            </span>
            <strong>{link.title}</strong>
            <p>{link.summary}</p>
            <span className="resource-footer">
              打开资料
              <ExternalLink aria-hidden="true" />
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

export function ChapterBookPage({ chapter }: { chapter: TravelChapter }) {
  return (
    <div className="day-page">
      <section className="hero-panel">
        <div className="hero-copy">
          <span className="day-marker">{chapter.marker}</span>
          <h1>{chapter.title}</h1>
          <p>{chapter.intro}</p>
          <div className="route-strip" aria-label={`${chapter.marker} route`}>
            {chapter.routeStops.map((stop, index) => (
              <span key={stop}>
                {stop}
                {index < chapter.routeStops.length - 1 && <ArrowRight aria-hidden="true" />}
              </span>
            ))}
          </div>
        </div>
        <figure className="hero-photo">
          <img key={chapter.heroImage} src={chapter.heroImage} alt={chapter.heroAlt} />
          <figcaption>{chapter.heroCredit}</figcaption>
        </figure>
      </section>

      <section className="book-spreads" aria-label={`${chapter.marker} book pages`}>
        {chapter.sections.map((section, index) => (
          <div className="spread-cluster" key={section.id}>
            <BookSpread index={index} section={section} />
          </div>
        ))}
      </section>

      <ChapterLibrary chapter={chapter} />

      <section className="discovery-panel">
        <div className="panel-heading">
          <MapPin aria-hidden="true" />
          <div>
            <h2>今天带走三个发现</h2>
            <p>不需要填满任务。选三个最想记住的线索，晚上讲给家人听。</p>
          </div>
        </div>
        <div className="discovery-grid">
          {chapter.sections.slice(0, 3).map((section) => (
            <div key={section.id}>
              <b>
                <Binoculars aria-hidden="true" />
                {section.eyebrow}
              </b>
              <p>{section.fieldQuestion ?? section.deck}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
